import { useEffect, useState } from 'react'

const SECOND_MS = 1_000
const MINUTE_MS = 60_000

/**
 * How long until the *displayed* age would change, given an age of `ageMs`.
 *
 * Labels built on this hook count seconds under a minute and whole minutes above it, so the cadence
 * splits at the same place the label does: once a second for the first minute, then once a minute
 * for as long as the screen stays open. Both branches align to the boundary rather than counting
 * from now, so the tick lands at the instant the text flips instead of drifting further into each
 * interval.
 *
 * The floors differ on purpose. A minute-tier wake is never worth scheduling sooner than a second
 * out; a second-tier one is allowed to be 50ms away, because that is what alignment costs when this
 * runs a hair late. Both exist only to stop a pathological timestamp from producing a busy loop.
 */
function msUntilNextBoundary(ageMs: number): number {
  if (ageMs < MINUTE_MS) return Math.max(50, SECOND_MS - (ageMs % SECOND_MS))
  return Math.max(SECOND_MS, MINUTE_MS - (ageMs % MINUTE_MS))
}

/**
 * Live age of a timestamp in milliseconds, or `null` if there is no timestamp yet.
 *
 * A self-rescheduling `setTimeout` rather than `setInterval`. The difference is not
 * micro-optimisation: it wakes exactly when the *label* changes and not once in between, which for
 * a timestamp older than a minute is once a minute rather than sixty times. Callers keep this hook
 * in the leaf that renders the age — the calendar header it was written for sits above three
 * columns of absolutely-positioned blocks and a live pointer-drag controller, and a 1 Hz `setState`
 * anywhere above that is a real jank risk on a phone.
 *
 * Nothing is scheduled while the tab is hidden. A background tab has no label to flip, and the
 * queries these ages describe stop polling there too (`refetchIntervalInBackground: false`), so a
 * timer would burn a phone's battery to keep a number nobody can read correct.
 *
 * The visibility and focus listeners are what make that safe, and they earn their keep twice over:
 * they restart the timer on wake, and they recompute the age immediately rather than waiting out a
 * timer that mobile browsers may have throttled or frozen. Without them a worker unlocking their
 * phone would read a stale age for up to a minute — precisely the moment the number matters most.
 */
export function useElapsedSince(since: number | null): number | null {
  const [ageMs, setAgeMs] = useState<number | null>(() =>
    since === null ? null : Math.max(0, Date.now() - since),
  )

  useEffect(() => {
    if (since === null) {
      setAgeMs(null)
      return
    }

    let timer: ReturnType<typeof setTimeout> | undefined

    const tick = () => {
      const age = Math.max(0, Date.now() - since)
      setAgeMs(age)
      // A hidden tab arms nothing; `resync` picks the cadence back up when it comes forward.
      if (document.hidden) return
      timer = setTimeout(tick, msUntilNextBoundary(age))
    }

    // Recompute immediately on wake rather than waiting out a timer that may not have run.
    const resync = () => {
      if (timer !== undefined) clearTimeout(timer)
      tick()
    }

    tick()
    document.addEventListener('visibilitychange', resync)
    window.addEventListener('focus', resync)

    return () => {
      if (timer !== undefined) clearTimeout(timer)
      document.removeEventListener('visibilitychange', resync)
      window.removeEventListener('focus', resync)
    }
  }, [since])

  return ageMs
}
